import { useState, useEffect, useCallback, useRef } from 'react';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Helper function to sanitize and format HTML content
const prepareHtmlContent = (content) => {
  if (!content) return '';
  
  let sanitized = content.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/ /g, ' ');
  if (!sanitized.includes('<p>') && !sanitized.includes('<h')) {
    sanitized = `<p>${sanitized}</p>`;
  }
  
  return sanitized;
};

// Helper function to format date to YYYY-MM-DD
const formatDateToYMD = (date) => {
  if (!date) return new Date().toISOString().split('T')[0];
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

// Debounce function to limit API calls
const useDebounce = (callback, delay = 500) => {
  const timeoutRef = useRef(null);
  
  return (...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};


// Helper function to check slug uniqueness
const checkSlugUniqueness = async (slug, language) => {
  if (!slug || slug.length < 3) return true; // Don't check too short slugs
  
  try {
    const response = await get(`/blog/check-slug?slug=${slug}&language=${language}`);
    return response.isUnique;
  } catch (err) {
    console.error('[BlogEditor] Error checking slug uniqueness:', err);
    // Pokud dostaneme 404 "Blog post not found", interpretujeme to jako "slug je dostupný"
    if (err.message && err.message.includes("Blog post not found")) {
      console.log(`[BlogEditor] Slug '${slug}' likely available (404 response). Assuming it's unique.`);
      return true;
    }
    // Pro ostatní chyby vrátíme raději false, což povede ke generování nového slugu
    return false;
  }
};

// Custom CSS styles for fixing Quill editor in dark mode admin
const quillStyles = `
.admin-quill-editor {
  min-height: 300px;
  margin-bottom: 20px;
}

.admin-quill-editor .ql-container {
  min-height: 250px;
  font-size: 16px;
}

.admin-quill-editor .ql-editor {
  min-height: 250px;
  max-height: 500px;
  overflow-y: auto;
  color: #333;
  background: white;
}

.admin-quill-editor .ql-toolbar {
  background: #f1f1f1;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.ql-snow .ql-picker {
  color: #333;
}

.ql-snow .ql-stroke {
  stroke: #333;
}

.ql-snow .ql-fill, .ql-snow .ql-stroke.ql-fill {
  fill: #333;
}

.ql-snow.ql-toolbar button:hover,
.ql-snow .ql-toolbar button:hover,
.ql-snow.ql-toolbar button:focus,
.ql-snow .ql-toolbar button:focus,
.ql-snow.ql-toolbar button.ql-active,
.ql-snow .ql-toolbar button.ql-active,
.ql-snow.ql-toolbar .ql-picker-label:hover,
.ql-snow .ql-toolbar .ql-picker-label:hover,
.ql-snow.ql-toolbar .ql-picker-label.ql-active,
.ql-snow .ql-toolbar .ql-picker-label.ql-active,
.ql-snow.ql-toolbar .ql-picker-item:hover,
.ql-snow .ql-toolbar .ql-picker-item:hover,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected {
  color: #b91c1c;
}

.ql-snow.ql-toolbar button:hover .ql-stroke,
.ql-snow .ql-toolbar button:hover .ql-stroke,
.ql-snow.ql-toolbar button:focus .ql-stroke,
.ql-snow .ql-toolbar button:focus .ql-stroke,
.ql-snow.ql-toolbar button.ql-active .ql-stroke,
.ql-snow .ql-toolbar button.ql-active .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
.ql-snow.ql-toolbar button:hover .ql-stroke-miter,
.ql-snow .ql-toolbar button:hover .ql-stroke-miter,
.ql-snow.ql-toolbar button:focus .ql-stroke-miter,
.ql-snow .ql-toolbar button:focus .ql-stroke-miter,
.ql-snow.ql-toolbar button.ql-active .ql-stroke-miter,
.ql-snow .ql-toolbar button.ql-active .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter {
  stroke: #b91c1c;
}

.ql-snow a {
  color: #b91c1c;
}
`;

const BlogEditor = () => {
  // Inject custom Quill editor styles once on component mount
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = quillStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [selectedImage, setSelectedImage] = useState(null);
  const [articleImages, setArticleImages] = useState([]);
  const [slugCache, setSlugCache] = useState({});
  const language = getUserLanguage();

  const [currentPost, setCurrentPost] = useState({
    id: null,
    image_path: '',
    published_date: new Date().toISOString().split('T')[0],
    is_active: true,
    display_order: 0,
    category: '',
    tags: '',
    translations: {
      en: { slug: '', title: '', summary: '', content: '' },
      cs: { slug: '', title: '', summary: '', content: '' },
    },
  });

  const editorModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      [{ 'align': [] }],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  const editorFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image',
    'color', 'background',
    'align'
  ];

  const getImageUrl = (path) => {
    if (!path) {
      console.log('[BlogEditor] No image path provided, using placeholder');
      return '/images/placeholder.png';
    }
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const fullUrl = `${IMAGE_BASE_URL}${normalizedPath}`;
    console.log('[BlogEditor] Generated image URL:', fullUrl);
    return fullUrl;
  };

// Toto je funkce, kterou potřebujete upravit v souboru BlogEditor.jsx

const fetchPosts = useCallback(async () => {
  setLoading(true);
  setError(null);
  try {
    const headers = { 'accept-language': language };
    
    // ZMĚNA: Přidán parametr admin=true, aby se zobrazily i neaktivní příspěvky pro administrátory
    const data = await get('/blog?limit=50&admin=true', headers);
    
    if (data && Array.isArray(data.posts)) {
      const processedData = data.posts.map(post => ({
        ...post,
        is_active: !!post.is_active,
        display_order: post.display_order || 0,
        translations: {
          [language]: {
            slug: post.slug || '',
            title: post.title || '',
            summary: post.summary || '',
            content: post.content || ''
          }
        }
      }));
      setPosts(processedData);
      console.log('[BlogEditor] Successfully fetched posts:', processedData.length);
    } else {
      console.error('[BlogEditor] Unexpected response format:', data);
      setPosts([]);
      setError(language === 'cs' ? 'Neočekávaný formát dat při načítání příspěvků' : 'Unexpected data format when loading posts');
    }
  } catch (err) {
    console.error('[BlogEditor] Error fetching posts:', err);
    setError(language === 'cs' ? `Nepodařilo se načíst příspěvky: ${err.message}` : `Failed to load posts: ${err.message}`);
    setPosts([]);
  } finally {
    setLoading(false);
  }
}, [language]);

  useEffect(() => {
    console.log('[BlogEditor] Fetching posts...');
    fetchPosts();
  }, [fetchPosts]);

  const handleChange = (e, lang = null) => {
    const { name, value, type, checked } = e.target;
    
    if (lang && ['title', 'summary', 'slug'].includes(name)) {
      setCurrentPost((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            [name]: value,
          },
        },
      }));
    } else {
      setCurrentPost((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleContentChange = (value, lang) => {
    console.log(`[BlogEditor] Content changed for ${lang}, length: ${value?.length || 0}`);
    const sanitizedContent = prepareHtmlContent(value);
    
    setCurrentPost((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          content: sanitizedContent,
        },
      },
    }));
  };

  const debugContent = (lang) => {
    const content = currentPost.translations[lang]?.content || '';
    console.log(`[DEBUG] ${lang.toUpperCase()} Content:`, { 
      length: content.length,
      firstChars: content.substring(0, 50),
      lastChars: content.substring(content.length - 50),
      hasHTML: content.includes('<') && content.includes('>'),
      hasParagraphs: content.includes('<p'),
      hasHeadings: content.includes('<h'),
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedImage(e.target.files[0]);
      console.log('[BlogEditor] Featured image selected:', e.target.files[0].name);
    }
  };

  const handleArticleImageUpload = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append('image', file);
      
      try {
        setLoading(true);
        const response = await post('/blog/images', formData);
        
        if (response && response.image_path) {
          const newImage = {
            name: file.name,
            path: response.image_path,
            url: getImageUrl(response.image_path)
          };
          
          setArticleImages(prev => [...prev, newImage]);
          setSuccess(language === 'cs' ? 'Obrázek úspěšně nahrán' : 'Image uploaded successfully');
          console.log('[BlogEditor] Image uploaded successfully:', newImage);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('[BlogEditor] Error uploading article image:', err);
        setError(language === 'cs' ? `Nepodařilo se nahrát obrázek: ${err.message}` : `Failed to upload image: ${err.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const insertImageToEditor = (imageUrl, lang) => {
    const imageHtml = `<img src="${imageUrl}" alt="Article image" class="blog-image" />`;
    
    setCurrentPost((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          content: (prev.translations[lang].content || '') + imageHtml,
        },
      },
    }));
    
    console.log(`[BlogEditor] Image inserted to ${lang} editor:`, imageUrl);
  };

  const resetForm = () => {
    setCurrentPost({
      id: null,
      image_path: '',
      published_date: new Date().toISOString().split('T')[0],
      is_active: true,
      display_order: 0,
      category: '',
      tags: '',
      translations: {
        en: { slug: '', title: '', summary: '', content: '' },
        cs: { slug: '', title: '', summary: '', content: '' },
      },
    });
    setSelectedImage(null);
    setArticleImages([]);
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
    setActiveTab('general');
    setSlugCache({});
    console.log('[BlogEditor] Form reset to initial state');
  };

  const handleAddNew = () => {
    console.log('[BlogEditor] Opening form for new post');
    resetForm();
    setShowForm(true);
  };

  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // Memoize the generateUniqueSlug function with useCallback
  const generateUniqueSlug = useCallback(async (baseSlug, language) => {
    if (!baseSlug) return '';
    
    // Use cached result if available and not in edit mode
    const cacheKey = `${baseSlug}-${language}`;
    if (slugCache[cacheKey]) {
      return slugCache[cacheKey];
    }
    
    let slug = baseSlug;
    let counter = 1;
    let isUnique = false;
    
    try {
      isUnique = await checkSlugUniqueness(slug, language);
      
      while (!isUnique && counter < 10) { // Limit to 10 attempts
        slug = `${baseSlug}-${counter}`;
        isUnique = await checkSlugUniqueness(slug, language);
        counter++;
      }
      
      // Cache the result
      setSlugCache(prev => ({
        ...prev,
        [cacheKey]: slug
      }));
      
      return slug;
    } catch (err) {
      console.error('[BlogEditor] Error generating unique slug:', err);
      // Return baseSlug with a random number as fallback
      const fallbackSlug = `${baseSlug}-${Math.floor(Math.random() * 1000)}`;
      return fallbackSlug;
    }
  }, [slugCache]);

  // Create a debounced version of the title change handler
  const debouncedHandleTitleChange = useDebounce((value, lang) => {
    if (!editMode && value.length > 2) { // Only generate slugs for titles longer than 2 characters
      const baseSlug = generateSlug(value);
      generateUniqueSlug(baseSlug, lang).then(newSlug => {
        setCurrentPost(prev => ({
          ...prev,
          translations: {
            ...prev.translations,
            [lang]: {
              ...prev.translations[lang],
              slug: newSlug
            }
          }
        }));
        console.log(`[BlogEditor] Auto-generated slug for ${lang}:`, newSlug);
      });
    }
  }, 1000); // Wait 1 second after the user stops typing before checking slug

  const handleTitleChange = (e, lang) => {
    const { value } = e.target;
    handleChange(e, lang);
    
    if (!editMode) {
      debouncedHandleTitleChange(value, lang);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!currentPost.translations.en.slug || !currentPost.translations.cs.slug) {
      setError(language === 'cs' ? 'URL slug je povinný pro oba jazyky' : 'URL slug is required for both languages');
      setActiveTab('general');
      return;
    }

    if (!currentPost.translations.en.title || !currentPost.translations.cs.title) {
      setError(language === 'cs' ? 'Název je povinný v obou jazycích' : 'Title is required in both languages');
      setActiveTab('general');
      return;
    }

    try {
      setLoading(true);
      
      const formData = new FormData();
      
      if (selectedImage) {
        formData.append('image', selectedImage);
        console.log('[BlogEditor] Appending new image:', selectedImage.name);
      } else if (editMode && currentPost.image_path) {
        formData.append('image_path', currentPost.image_path);
        console.log('[BlogEditor] Appending existing image_path:', currentPost.image_path);
      }

      // Format published_date to YYYY-MM-DD
      const formattedDate = formatDateToYMD(currentPost.published_date);
      formData.append('published_date', formattedDate);
      formData.append('is_active', currentPost.is_active ? 'true' : 'false');
      formData.append('display_order', currentPost.display_order.toString());
      formData.append('category', currentPost.category || 'uncategorized');
      formData.append('tags', currentPost.tags || '');
      
      const translations = {
        en: {
          slug: currentPost.translations.en.slug,
          title: currentPost.translations.en.title,
          summary: currentPost.translations.en.summary || '',
          content: currentPost.translations.en.content || ''
        },
        cs: {
          slug: currentPost.translations.cs.slug,
          title: currentPost.translations.cs.title,
          summary: currentPost.translations.cs.summary || '',
          content: currentPost.translations.cs.content || ''
        }
      };
      
      formData.append('translations', JSON.stringify(translations));
      
      console.log('[BlogEditor] Submitting post data:', {
        mode: editMode ? 'edit' : 'create',
        id: currentPost.id,
        published_date: formattedDate,
        hasImage: !!selectedImage || !!currentPost.image_path,
        translations
      });

      let response;
      if (editMode) {
        response = await put(`/blog/${currentPost.id}`, formData);
        console.log('[BlogEditor] Update response:', response);
        setSuccess(language === 'cs' ? 'Příspěvek úspěšně aktualizován' : 'Post updated successfully');
      } else {
        response = await post('/blog', formData);
        console.log('[BlogEditor] Create response:', response);
        setSuccess(language === 'cs' ? 'Příspěvek úspěšně přidán' : 'Post added successfully');
      }
      
      resetForm();
      fetchPosts();
    } catch (err) {
      console.error('[BlogEditor] Error saving post:', err);
      setError(language === 'cs' ? `Nepodařilo se uložit příspěvek: ${err.message}` : `Failed to save post: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPostTranslations = async (id) => {
    try {
      const response = await get(`/blog/id/${id}`);
      
      console.log('[BlogEditor] Raw API response:', JSON.stringify(response, null, 2));
      
      if (!response || !response.id) {
        throw new Error('No response or invalid response received from API');
      }
      
      // Mapování překladů z API odpovědi
      const translations = {
        en: {
          slug: response.translations?.en?.slug || '',
          title: response.translations?.en?.title || '',
          summary: response.translations?.en?.summary || '',
          content: response.translations?.en?.content || ''
        },
        cs: {
          slug: response.translations?.cs?.slug || '',
          title: response.translations?.cs?.title || '',
          summary: response.translations?.cs?.summary || '',
          content: response.translations?.cs?.content || ''
        }
      };
      
      // Varování, pokud je anglický překlad prázdný
      if (!translations.en.title && editMode) {
        console.warn('[BlogEditor] English translation is empty for post ID:', id);
        setError(language === 'cs' ? 'Anglický překlad je prázdný, prosím vyplňte název' : 'English translation is empty, please fill in the title');
      }
      
      console.log('[BlogEditor] Processed translations:', translations);
      
      return {
        id: response.id,
        image_path: response.image_path || '',
        published_date: formatDateToYMD(response.published_date),
        is_active: response.is_active === 1 || response.is_active === true,
        display_order: response.display_order || 0,
        category: response.category || '',
        tags: response.tags || '',
        translations
      };
    } catch (err) {
      console.error('[BlogEditor] Error in fetchPostTranslations:', err);
      throw new Error(`Failed to fetch post translations: ${err.message}`);
    }
  };

  const handleEdit = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[BlogEditor] Fetching post with ID:', id);
      const post = await fetchPostTranslations(id);
      
      if (!post || !post.id) {
        throw new Error('Invalid post data received');
      }
      
      console.log('[BlogEditor] Fetched post for editing:', post);
      
      setCurrentPost({
        id: post.id,
        image_path: post.image_path,
        published_date: post.published_date,
        is_active: post.is_active,
        display_order: post.display_order,
        category: post.category,
        tags: post.tags,
        translations: post.translations
      });
      setEditMode(true);
      setSelectedImage(null);
      setShowForm(true);
      setActiveTab('general');
      
      try {
        const imagesResponse = await get(`/blog/${id}/images`, { 'accept-language': language });
        if (imagesResponse && Array.isArray(imagesResponse.images)) {
          const formattedImages = imagesResponse.images.map(img => ({
            name: img.name || 'Image',
            path: img.path,
            url: getImageUrl(img.path)
          }));
          setArticleImages(formattedImages);
          console.log('[BlogEditor] Loaded post images:', formattedImages.length);
        }
      } catch (imgErr) {
        console.error('[BlogEditor] Error fetching post images:', imgErr);
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[BlogEditor] Error fetching post for edit:', err);
      setError(language === 'cs' ? `Nepodařilo se načíst příspěvek pro úpravu: ${err.message}` : `Failed to load post for editing: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tento příspěvek?' : 'Are you sure you want to delete this post?')) {
      return;
    }
    
    try {
      setLoading(true);
      await del(`/blog/${id}`);
      setSuccess(language === 'cs' ? 'Příspěvek úspěšně smazán' : 'Post deleted successfully');
      fetchPosts();
    } catch (err) {
      console.error('[BlogEditor] Error deleting post:', err);
      setError(language === 'cs' ? `Nepodařilo se smazat příspěvek: ${err.message}` : `Failed to delete post: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="admin-grid admin-grid-cols-1 admin-md:admin-grid-cols-2 admin-gap-6">
            <div>
              <div className="admin-mb-4">
                <label className="admin-form-label">{language === 'cs' ? 'Kategorie' : 'Category'}</label>
                <input
                  type="text"
                  name="category"
                  className="admin-form-input"
                  value={currentPost.category}
                  onChange={handleChange}
                  placeholder={language === 'cs' ? 'např. novinky, závody, technika' : 'e.g. news, races, tech'}
                />
              </div>
              <div className="admin-mb-4">
                <label className="admin-form-label">{language === 'cs' ? 'Tagy (oddělené čárkou)' : 'Tags (comma separated)'}</label>
                <input
                  type="text"
                  name="tags"
                  className="admin-form-input"
                  value={currentPost.tags}
                  onChange={handleChange}
                  placeholder={language === 'cs' ? 'např. drift, závod, bmw' : 'e.g. drift, race, bmw'}
                />
              </div>
              <div className="admin-mb-4">
                <label className="admin-form-label">{language === 'cs' ? 'Datum publikace' : 'Publication Date'}</label>
                <input
                  type="date"
                  name="published_date"
                  className="admin-form-input"
                  value={currentPost.published_date}
                  onChange={handleChange}
                />
              </div>
              <div className="admin-mb-4">
                <label className="admin-flex admin-items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    className="admin-form-checkbox"
                    checked={currentPost.is_active}
                    onChange={handleChange}
                  />
                  <span className="admin-ml-2">{language === 'cs' ? 'Publikováno' : 'Published'}</span>
                </label>
              </div>
              <div className="admin-mb-4">
                <label className="admin-form-label">
                  {language === 'cs' ? 'Náhledový obrázek' : 'Featured Image'}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="admin-form-input"
                  onChange={handleImageChange}
                />
                {editMode && currentPost.image_path && !selectedImage && (
                  <div className="admin-mt-2">
                    <img
                      src={getImageUrl(currentPost.image_path)}
                      alt="Preview"
                      className="admin-h-32 admin-w-auto admin-object-cover admin-rounded"
                      onError={(e) => {
                        console.error('[BlogEditor] Image load error:', currentPost.image_path);
                        e.target.src = '/images/placeholder.png';
                      }}
                    />
                  </div>
                )}
                {selectedImage && (
                  <div className="admin-mt-2">
                    <img
                      src={URL.createObjectURL(selectedImage)}
                      alt="Preview"
                      className="admin-h-32 admin-w-auto admin-object-cover admin-rounded"
                    />
                  </div>
                )}
              </div>
            </div>
            <div>
              <h3 className="admin-text-xl admin-font-medium admin-mb-4">English</h3>
              <div className="admin-mb-4">
                <label className="admin-form-label">
                  URL Slug <span className="admin-text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  className="admin-form-input"
                  value={currentPost.translations.en.slug}
                  onChange={(e) => handleChange(e, 'en')}
                  required
                  disabled={editMode}
                />
                <p className="admin-text-xs admin-text-gray-400 admin-mt-1">
                  {language === 'cs' 
                    ? 'URL adresa příspěvku bez mezer a speciálních znaků (automaticky generována z názvu)' 
                    : 'Post URL without spaces and special characters (auto-generated from title)'}
                </p>
              </div>
              <div className="admin-mb-4">
                <label className="admin-form-label">
                  Title <span className="admin-text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  className="admin-form-input"
                  value={currentPost.translations.en.title}
                  onChange={(e) => handleTitleChange(e, 'en')}
                  required
                />
              </div>
              <div className="admin-mb-4">
                <label className="admin-form-label">Short Description</label>
                <textarea
                  name="summary"
                  className="admin-form-textarea"
                  value={currentPost.translations.en.summary}
                  onChange={(e) => handleChange(e, 'en')}
                  rows="3"
                />
              </div>
              <h3 className="admin-text-xl admin-font-medium admin-mb-4">Čeština</h3>
              <div className="admin-mb-4">
                <label className="admin-form-label">
                  URL Slug <span className="admin-text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="slug"
                  className="admin-form-input"
                  value={currentPost.translations.cs.slug}
                  onChange={(e) => handleChange(e, 'cs')}
                  required
                  disabled={editMode}
                />
                <p className="admin-text-xs admin-text-gray-400 admin-mt-1">
                  {language === 'cs' 
                    ? 'URL adresa příspěvku bez mezer a speciálních znaků (automaticky generována z názvu)' 
                    : 'Post URL without spaces and special characters (auto-generated from title)'}
                </p>
              </div>
              <div className="admin-mb-4">
                <label className="admin-form-label">
                  Název <span className="admin-text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  className="admin-form-input"
                  value={currentPost.translations.cs.title}
                  onChange={(e) => handleTitleChange(e, 'cs')}
                  required
                />
              </div>
              <div className="admin-mb-4">
                <label className="admin-form-label">Krátký popis</label>
                <textarea
                  name="summary"
                  className="admin-form-textarea"
                  value={currentPost.translations.cs.summary}
                  onChange={(e) => handleChange(e, 'cs')}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      case 'content-en':
        return (
          <div className="admin-w-full">
            <h3 className="admin-text-xl admin-font-medium admin-mb-4">English Content</h3>
            <div className="admin-mb-4">
              <label className="admin-form-label">Add Images</label>
              <div className="admin-flex admin-items-center">
                <input
                  type="file"
                  accept="image/*"
                  className="admin-form-input admin-mr-2"
                  onChange={handleArticleImageUpload}
                />
              </div>
              {articleImages.length > 0 && (
                <div className="admin-mt-4">
                  <h4 className="admin-text-lg admin-font-medium admin-mb-2">Available Images</h4>
                  <div className="admin-grid admin-grid-cols-2 admin-md:admin-grid-cols-4 admin-gap-4">
                    {articleImages.map((img, index) => (
                      <div key={index} className="admin-bg-gray-700 admin-rounded admin-p-2">
                        <img 
                          src={img.url} 
                          alt={img.name} 
                          className="admin-h-24 admin-w-full admin-object-cover admin-rounded admin-mb-2" 
                        />
                        <div className="admin-flex admin-justify-between admin-items-center">
                          <span className="admin-text-xs admin-truncate admin-w-24">{img.name}</span>
                          <button 
                            type="button"
                            onClick={() => insertImageToEditor(img.url, 'en')}
                            className="admin-text-xs admin-bg-blue-500 admin-text-white admin-px-2 admin-py-1 admin-rounded hover:admin-bg-blue-600"
                          >
                            Insert
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="admin-mb-4">
              <div className="admin-bg-white admin-rounded">
                <ReactQuill 
                  theme="snow"
                  value={currentPost.translations.en.content}
                  onChange={(content) => handleContentChange(content, 'en')}
                  modules={editorModules}
                  formats={editorFormats}
                  placeholder="Write or paste HTML content here..."
                  className="admin-quill-editor"
                />
              </div>
              <p className="admin-text-xs admin-text-gray-400 admin-mt-1">
                Use the toolbar above to format text and add images, or paste HTML content directly.
              </p>
            </div>
            <div className="admin-flex admin-justify-between admin-items-center admin-text-xs admin-text-gray-400 admin-mt-2">
              <span>Content length: {currentPost.translations.en.content?.length || 0} characters</span>
              <button
                type="button"
                onClick={() => debugContent('en')}
                className="admin-text-xs admin-bg-gray-700 admin-text-gray-300 admin-px-2 admin-py-1 admin-rounded"
              >
                Debug Content
              </button>
            </div>
          </div>
        );
      case 'content-cs':
        return (
          <div className="admin-w-full">
            <h3 className="admin-text-xl admin-font-medium admin-mb-4">Český obsah</h3>
            <div className="admin-mb-4">
              <label className="admin-form-label">Přidat obrázky</label>
              <div className="admin-flex admin-items-center">
                <input
                  type="file"
                  accept="image/*"
                  className="admin-form-input admin-mr-2"
                  onChange={handleArticleImageUpload}
                />
              </div>
              {articleImages.length > 0 && (
                <div className="admin-mt-4">
                  <h4 className="admin-text-lg admin-font-medium admin-mb-2">Dostupné obrázky</h4>
                  <div className="admin-grid admin-grid-cols-2 admin-md:admin-grid-cols-4 admin-gap-4">
                    {articleImages.map((img, index) => (
                      <div key={index} className="admin-bg-gray-700 admin-rounded admin-p-2">
                        <img 
                          src={img.url} 
                          alt={img.name} 
                          className="admin-h-24 admin-w-full admin-object-cover admin-rounded admin-mb-2" 
                        />
                        <div className="admin-flex admin-justify-between admin-items-center">
                          <span className="admin-text-xs admin-truncate admin-w-24">{img.name}</span>
                          <button 
                            type="button"
                            onClick={() => insertImageToEditor(img.url, 'cs')}
                            className="admin-text-xs admin-bg-blue-500 admin-text-white admin-px-2 admin-py-1 admin-rounded hover:admin-bg-blue-600"
                          >
                            Vložit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="admin-mb-4">
              <div className="admin-bg-white admin-rounded">
                <ReactQuill 
                  theme="snow"
                  value={currentPost.translations.cs.content}
                  onChange={(content) => handleContentChange(content, 'cs')}
                  modules={editorModules}
                  formats={editorFormats}
                  placeholder="Napište nebo vložte HTML obsah zde..."
                  className="admin-quill-editor"
                />
              </div>
              <p className="admin-text-xs admin-text-gray-400 admin-mt-1">
                Použijte panel nástrojů výše pro formátování textu a přidání obrázků, nebo vložte HTML obsah přímo.
              </p>
            </div>
            <div className="admin-flex admin-justify-between admin-items-center admin-text-xs admin-text-gray-400 admin-mt-2">
              <span>Délka obsahu: {currentPost.translations.cs.content?.length || 0} znaků</span>
              <button
                type="button"
                onClick={() => debugContent('cs')}
                className="admin-text-xs admin-bg-gray-700 admin-text-gray-300 admin-px-2 admin-py-1 admin-rounded"
              >
                Debug obsahu
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa blogu' : 'Blog Management'}</h1>
        {!showForm && (
          <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
            <i className="fas fa-plus admin-mr-2"></i>
            {language === 'cs' ? 'Přidat příspěvek' : 'Add Post'}
          </button>
        )}
      </div>

      {error && (
        <div className="admin-alert admin-alert-error admin-mb-4">
          <i className="fas fa-exclamation-triangle admin-mr-2"></i>
          {error}
        </div>
      )}
      
      {success && (
        <div className="admin-alert admin-alert-success admin-mb-4">
          <i className="fas fa-check-circle admin-mr-2"></i>
          {success}
        </div>
      )}

      {loading && (
        <div className="admin-flex admin-justify-center admin-items-center admin-p-4 admin-mb-4 admin-bg-gray-800 admin-rounded">
          <svg className="admin-animate-spin admin-h-6 admin-w-6 admin-text-red-500 admin-mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="admin-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="admin-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <span>{language === 'cs' ? 'Načítání...' : 'Loading...'}</span>
        </div>
      )}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray admin-flex admin-justify-between admin-items-center">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode
                ? language === 'cs'
                  ? `Upravit příspěvek: ${currentPost.translations[language]?.title || currentPost.translations.en.title || ''}`
                  : `Edit Post: ${currentPost.translations[language]?.title || currentPost.translations.en.title || ''}`
                : language === 'cs'
                ? 'Přidat příspěvek'
                : 'Add Post'}
            </h2>
            <button
              type="button"
              className="admin-text-gray-400 hover:admin-text-white"
              onClick={resetForm}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="admin-p-4 admin-bg-gray-800 admin-rounded-b">
            <div className="admin-flex admin-border-b admin-border-gray-700 admin-mb-4">
              <button
                className={`admin-px-4 admin-py-2 admin-mr-2 admin-font-medium ${
                  activeTab === 'general' ? 'admin-text-red-500 admin-border-b-2 admin-border-red-500' : 'admin-text-gray-400'
                }`}
                onClick={() => setActiveTab('general')}
              >
                <i className="fas fa-info-circle admin-mr-2"></i>
                {language === 'cs' ? 'Obecné informace' : 'General Info'}
              </button>
              <button
                className={`admin-px-4 admin-py-2 admin-mr-2 admin-font-medium ${
                  activeTab === 'content-en' ? 'admin-text-red-500 admin-border-b-2 admin-border-red-500' : 'admin-text-gray-400'
                }`}
                onClick={() => setActiveTab('content-en')}
              >
                <i className="fas fa-pen admin-mr-2"></i>
                {language === 'cs' ? 'Obsah (EN)' : 'Content (EN)'}
                {currentPost.translations.en.content ? 
                  <span className="admin-bg-green-500 admin-text-white admin-text-xs admin-px-1 admin-py-0.5 admin-rounded admin-ml-2">✓</span> : 
                  null}
              </button>
              <button
                className={`admin-px-4 admin-py-2 admin-mr-2 admin-font-medium ${
                  activeTab === 'content-cs' ? 'admin-text-red-500 admin-border-b-2 admin-border-red-500' : 'admin-text-gray-400'
                }`}
                onClick={() => setActiveTab('content-cs')}
              >
                <i className="fas fa-pen admin-mr-2"></i>
                {language === 'cs' ? 'Obsah (CS)' : 'Content (CS)'}
                {currentPost.translations.cs.content ? 
                  <span className="admin-bg-green-500 admin-text-white admin-text-xs admin-px-1 admin-py-0.5 admin-rounded admin-ml-2">✓</span> : 
                  null}
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              {renderTabContent()}
              <div className="admin-mt-6 admin-flex admin-items-center admin-border-t admin-border-gray-700 admin-pt-4">
                <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                  <i className={`fas ${editMode ? 'fa-save' : 'fa-plus'} admin-mr-2`}></i>
                  {editMode
                    ? language === 'cs'
                      ? 'Aktualizovat'
                      : 'Update'
                    : language === 'cs'
                    ? 'Přidat'
                    : 'Add'}
                </button>
                <button 
                  type="button" 
                  className="admin-btn admin-btn-secondary" 
                  onClick={resetForm}
                >
                  <i className="fas fa-times admin-mr-2"></i>
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray admin-flex admin-justify-between admin-items-center">
          <h2 className="admin-font-medium">{language === 'cs' ? 'Seznam příspěvků' : 'Posts List'}</h2>
          <span className="admin-text-sm admin-text-gray-400">
            {posts.length} {language === 'cs' ? 'příspěvků' : 'posts'}
          </span>
        </div>
          {posts.length === 0 && !loading ? (
            <div className="admin-p-4 admin-text-center admin-text-gray-300">
              {language === 'cs' ? 'Žádné příspěvky nebyly nalezeny.' : 'No posts found.'}
            </div>
          ) : (
            <div className="admin-overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr className="admin-bg-accent-gray">
                    <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Název' : 'Title'}</th>
                    <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Datum' : 'Date'}</th>
                    <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Kategorie' : 'Category'}</th>
                    <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Status' : 'Status'}</th>
                    <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id} className={`admin-table-row ${!post.is_active ? 'admin-bg-gray-700' : ''}`}>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-flex admin-items-center">
                          <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                            {post.image_path ? (
                              <img
                                className="admin-h-10 admin-w-10 admin-rounded-full admin-object-cover"
                                src={getImageUrl(post.image_path)}
                                alt={post.translations?.[language]?.title || post.translations?.en?.title || 'Post'}
                                onError={(e) => {
                                  console.error('[BlogEditor] Table image load error:', post.image_path);
                                  e.target.src = '/images/placeholder.png';
                                }}
                              />
                            ) : (
                              <div className="admin-h-10 admin-w-10 admin-rounded-full admin-bg-gray-600 admin-flex admin-items-center admin-justify-center">
                                <i className="fas fa-image admin-text-gray-400"></i>
                              </div>
                            )}
                          </div>
                          <div className="admin-ml-4">
                            <div className="admin-text-sm admin-font-medium admin-text-white admin-truncate admin-max-w-xs">
                              {!post.is_active && (
                                <span className="admin-text-red-500 admin-font-bold admin-mr-1">[DRAFT] </span>
                              )}
                              {post.translations?.[language]?.title || post.translations?.en?.title || (language === 'cs' ? 'Bez názvu' : 'Untitled')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-gray-300">
                          {new Date(post.published_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-gray-300">{post.category || '-'}</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <span
                          className={`admin-px-2 admin-py-1 admin-text-xs admin-rounded-full ${
                            post.is_active
                              ? 'admin-bg-green-900 admin-text-green-300'
                              : 'admin-bg-red-900 admin-text-red-300'
                          }`}
                        >
                          {post.is_active
                            ? language === 'cs'
                              ? 'Publikováno'
                              : 'Published'
                            : language === 'cs'
                            ? 'Koncept'
                            : 'Draft'}
                        </span>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-flex admin-space-x-3">
                          <button
                            onClick={() => handleEdit(post.id)}
                            className="admin-text-blue-500 hover:admin-text-blue-400"
                            title={language === 'cs' ? 'Upravit' : 'Edit'}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          {post.is_active && (
                            <a
                              href={`/blog/${post.translations?.[language]?.slug || post.translations?.en?.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="admin-text-green-500 hover:admin-text-green-400"
                              title={language === 'cs' ? 'Zobrazit' : 'View'}
                            >
                              <i className="fas fa-eye"></i>
                            </a>
                          )}
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="admin-text-red-500 hover:admin-text-red-400"
                            title={language === 'cs' ? 'Smazat' : 'Delete'}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};

export default BlogEditor;